/**
 * How an InOut of the person's history is said — one reading for Citizen Web
 * and the phone (InOut history, 22 Oct 2026):
 *
 *   - `title`   its title's name, in the viewer's language — or that it holds
 *               none;
 *   - `period`  its title's dates in words ("2 Mar 2025 – 1 Mar 2027"), from
 *               the issue date when the title has no end;
 *   - `state`   the principal's chip ("Active" / "Paused") and since when;
 *   - `closed`  when and why it left the principal's place — a new InOut took
 *               it, another one was restored, the person closed it, or it was
 *               kept from before InOut had a history;
 *   - `holds`   what it holds, one phrase each — trips, days away, documents.
 *
 * A moment (when it was closed, paused) is said as the day it fell on, on
 * this device (`inoutLocalDay`); the title's dates are calendar days. Every
 * word is a literal key.
 *
 * And what keeps an action on it from being taken (`inoutHistoryBlocks`),
 * known before it is pressed: the principal's case with the advisory firm is
 * open (nothing takes its place, D2-B); the InOut holds no title (nothing to
 * count again); a case was opened on it (its dossier reads it).
 */
import type { InOutRecordSummary } from '@tonnie/types';
import type { TFunction } from 'i18next';

/** The calendar day a moment falls on, on this device — `YYYY-MM-DD` in its own time zone. */
export function inoutLocalDay(moment: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`;
}

/** The principal — the InOut running or paused, if any — and the closed ones, as the server orders them. */
export function inoutHistoryParts(records: readonly InOutRecordSummary[]): {
  principal: InOutRecordSummary | null;
  closed: InOutRecordSummary[];
} {
  return {
    principal: records.find((record) => record.state !== 'closed') ?? null,
    closed: records.filter((record) => record.state === 'closed'),
  };
}

/** Whether the person keeps any closed InOut — the start offers to restore one. */
export function inoutHasClosedRecords(records: readonly InOutRecordSummary[] | undefined): boolean {
  return (records ?? []).some((record) => record.state === 'closed');
}

export function inoutHistoryWords({
  t,
  formatDay,
  formatNumber,
}: {
  t: TFunction;
  /** A calendar day (`YYYY-MM-DD`) in words. */
  formatDay: (calendarDay: string) => string;
  formatNumber: (value: number) => string;
}) {
  /** The day a moment fell on, on this device, in words. */
  const dayOf = (moment: string) => formatDay(inoutLocalDay(new Date(moment)));

  const title = (record: InOutRecordSummary) => record.regimeLabel ?? t('inout.history.noTitle');

  const period = (record: InOutRecordSummary): string | null => {
    if (!record.issuedOn) return null;
    return record.validUntil
      ? t('inout.history.period', {
          from: formatDay(record.issuedOn),
          to: formatDay(record.validUntil),
        })
      : t('inout.history.periodOpen', { from: formatDay(record.issuedOn) });
  };

  const state = (record: InOutRecordSummary): { chip: string; since: string } | null => {
    switch (record.state) {
      case 'active':
        return {
          chip: t('inout.history.stateActive'),
          since: t('inout.history.sinceActive', { date: dayOf(record.activatedAt) }),
        };
      case 'paused':
        return {
          chip: t('inout.history.statePaused'),
          since: t('inout.history.sincePaused', {
            date: dayOf(record.deactivatedAt ?? record.activatedAt),
          }),
        };
      case 'closed':
        return null;
      default: {
        const unreachable: never = record.state;
        throw new Error(`Unknown InOut state: ${String(unreachable)}`);
      }
    }
  };

  const closed = (record: InOutRecordSummary): string | null => {
    if (record.closedReason === null || record.closedAt === null) return null;
    const date = dayOf(record.closedAt);
    switch (record.closedReason) {
      case 'REPLACED':
        return t('inout.history.closedReplaced', { date });
      case 'RESTORED_OTHER':
        return t('inout.history.closedRestoredOther', { date });
      case 'CLOSED':
        return t('inout.history.closedByYou', { date });
      case 'LEGACY':
        return t('inout.history.closedLegacy');
      default: {
        const unreachable: never = record.closedReason;
        throw new Error(`Unknown closed reason: ${String(unreachable)}`);
      }
    }
  };

  const holds = (record: InOutRecordSummary) => ({
    trips: t('inout.history.trips', {
      count: record.holds.trips,
      trips: formatNumber(record.holds.trips),
    }),
    daysAway: t('inout.history.daysAway', {
      count: record.holds.daysAway,
      days: formatNumber(record.holds.daysAway),
    }),
    documents: t('inout.history.documents', {
      count: record.holds.documents,
      documents: formatNumber(record.holds.documents),
    }),
  });

  return { title, period, state, closed, holds };
}

/** Why each action is not offered for this InOut of the history — none when both are. */
export function inoutHistoryBlocks(
  record: InOutRecordSummary,
  principal: InOutRecordSummary | null,
  t: TFunction
): { restore: string | null; remove: string | null; notes: string[] } {
  const restore =
    principal?.openCase === true
      ? t('inout.errors.caseOpenChange')
      : record.regime === null
        ? t('inout.errors.noTitle')
        : null;
  const remove = record.usedByCase ? t('inout.errors.recordInUse') : null;
  return { restore, remove, notes: [restore, remove].filter((note) => note !== null) };
}
